import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { User, Building2, KeyRound, ShieldCheck, Clock, CheckCircle2, XCircle, RefreshCw, Camera, Pencil, Phone, MapPin } from "lucide-react";

const ROLE_META = {
  customer: { icon: User, label: "Customer", desc: "Browse & enquire" },
  broker: { icon: Building2, label: "Mediator", desc: "List properties (subscription)" },
  owner: { icon: KeyRound, label: "Owner", desc: "List your own property (per-listing fee)" },
};

export default function Account() {
  const { user, setUser } = useAuth();
  const { feature } = useSettings();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contact, setContact] = useState({ phone: user?.phone || "", address: user?.address || "" });
  const [savingContact, setSavingContact] = useState(false);

  const loadReq = () => api.get("/auth/role-request/me").then((r) => setRequest(r.data.request)).catch(() => {});
  useEffect(() => { loadReq(); }, []);

  if (!user) return null;

  const resolve = (ph) => (ph ? (ph.startsWith("http") ? ph : `${process.env.REACT_APP_BACKEND_URL}${ph}`) : "");
  const photoSrc = resolve(user.picture);
  const canUploadPhoto = user.role === "broker" || user.role === "owner";
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoLoading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/auth/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, picture: data.picture });
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setPhotoLoading(false); }
  };

  const currentRole = user.role;
  const canEditProfile = user.role === "broker" && feature("broker_profile_edit");
  const startEdit = () => { setForm({ name: user.name || "", phone: user.phone || "" }); setEditing(true); };
  const saveProfile = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/profile", { name: form.name, phone: form.phone });
      setUser({ ...user, name: data.name, phone: data.phone });
      setEditing(false);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingProfile(false); }
  };
  const startEditContact = () => { setContact({ phone: user.phone || "", address: user.address || "" }); setEditingContact(true); };
  const saveContact = async () => {
    setSavingContact(true);
    try {
      const { data } = await api.put("/auth/contact", { phone: contact.phone, address: contact.address });
      setUser({ ...user, phone: data.phone, address: data.address });
      setEditingContact(false);
      toast.success("Contact details updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSavingContact(false); }
  };
  const options = Object.keys(ROLE_META).filter((r) => r !== currentRole);
  const pending = request && request.status === "pending";

  const submit = async () => {
    if (!selected) { toast.error("Please choose a role to switch to."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/role-request", { requested_role: selected });
      setRequest(data);
      setSelected("");
      toast.success("Request submitted. An admin will review it shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-10" data-testid="account-page">
      <h1 className="font-heading font-bold text-3xl text-brand-ink">My Account</h1>
      <p className="text-brand-muted text-sm mt-1 mb-8">Manage your profile and role.</p>

      {/* Profile card */}
      <div className="bg-card border border-brand-line rounded-2xl p-5 mb-6" data-testid="account-profile">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-brand-emerald/10 flex items-center justify-center shrink-0 overflow-hidden border border-brand-line">
            {photoSrc ? <img src={photoSrc} alt="" className="w-16 h-16 object-cover" data-testid="account-photo" /> : <User className="w-8 h-8 text-brand-emerald" />}
          </div>
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="space-y-2" data-testid="account-edit-form">
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Display name" data-testid="account-edit-name" />
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" data-testid="account-edit-phone" />
                <div className="text-xs text-brand-muted truncate">{user.email}</div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover" data-testid="account-save-profile">{savingProfile ? "Saving..." : "Save"}</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={savingProfile} className="rounded-full" data-testid="account-cancel-profile">Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="font-heading font-semibold text-brand-ink text-lg truncate">{user.name}</div>
                  {canEditProfile && (
                    <button onClick={startEdit} className="text-brand-muted hover:text-brand-emerald shrink-0" title="Edit profile" data-testid="account-edit-profile"><Pencil className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="text-sm text-brand-muted truncate">{user.email}</div>
                {user.phone && <div className="text-sm text-brand-muted">{user.phone}</div>}
                {canUploadPhoto && (
                  <label className="mt-2 inline-flex items-center gap-2 cursor-pointer text-sm text-brand-emerald font-medium hover:underline" data-testid="account-photo-upload-label">
                    <Camera className="w-4 h-4" /> {photoLoading ? "Uploading..." : photoSrc ? "Update photo" : "Upload photo"}
                    <input type="file" accept="image/*" className="hidden" disabled={photoLoading} onChange={uploadPhoto} data-testid="account-photo-upload" />
                  </label>
                )}
              </>
            )}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-brand-line flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-brand-emerald" />
          <span className="text-sm text-brand-muted">Current role:</span>
          <span className="text-sm font-semibold text-brand-ink capitalize" data-testid="account-current-role">{ROLE_META[currentRole]?.label || currentRole}</span>
        </div>
      </div>

      {/* Communication details — editable by any user, only for their own account */}
      {feature("communication_profile") && (
      <div className="bg-card border border-brand-line rounded-2xl p-5 mb-6" data-testid="account-contact">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="font-heading font-semibold text-lg text-brand-ink flex items-center gap-2"><Phone className="w-5 h-5 text-brand-emerald" /> Communication Details</h2>
          {!editingContact && (
            <button onClick={startEditContact} className="text-brand-muted hover:text-brand-emerald shrink-0" title="Edit contact details" data-testid="account-edit-contact"><Pencil className="w-4 h-4" /></button>
          )}
        </div>
        <p className="text-sm text-brand-muted mb-4">Your contact number and address. Only you can edit these; our team may use them to reach you about your properties and enquiries.</p>
        {editingContact ? (
          <div className="space-y-3" data-testid="account-contact-form">
            <div>
              <label className="text-xs font-medium text-brand-muted flex items-center gap-1.5 mb-1"><Phone className="w-3.5 h-3.5" /> Contact number</label>
              <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="e.g. 98765 43210" data-testid="account-contact-phone" />
            </div>
            <div>
              <label className="text-xs font-medium text-brand-muted flex items-center gap-1.5 mb-1"><MapPin className="w-3.5 h-3.5" /> Communication address</label>
              <textarea value={contact.address} onChange={(e) => setContact({ ...contact, address: e.target.value })} placeholder="House / street, area, city, pincode" rows={3} className="w-full rounded-xl border border-brand-line bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-emerald/40" data-testid="account-contact-address" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveContact} disabled={savingContact} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover" data-testid="account-save-contact">{savingContact ? "Saving..." : "Save"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditingContact(false)} disabled={savingContact} className="rounded-full" data-testid="account-cancel-contact">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-brand-ink" data-testid="account-contact-phone-value"><Phone className="w-4 h-4 text-brand-muted shrink-0" />{user.phone || <span className="text-brand-muted">No contact number added</span>}</div>
            <div className="flex items-start gap-2 text-sm text-brand-ink" data-testid="account-contact-address-value"><MapPin className="w-4 h-4 text-brand-muted shrink-0 mt-0.5" />{user.address || <span className="text-brand-muted">No address added</span>}</div>
          </div>
        )}
      </div>
      )}

      {/* Role switch */}
      <div className="bg-card border border-brand-line rounded-2xl p-5" data-testid="account-role-switch">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="font-heading font-semibold text-lg text-brand-ink flex items-center gap-2"><RefreshCw className="w-5 h-5 text-brand-emerald" /> Switch Role</h2>
          <button onClick={loadReq} className="text-brand-muted hover:text-brand-emerald" title="Refresh" data-testid="account-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <p className="text-sm text-brand-muted mb-4">You can hold only one role at a time. Switching requires admin approval; your role updates on your next login after approval.</p>

        {!feature("role_switch_request") ? (
          <div className="rounded-xl bg-secondary border border-brand-line p-4 text-sm text-brand-muted" data-testid="account-role-switch-disabled">
            Role switching is currently unavailable. Please contact support if you need to change your account role.
          </div>
        ) : pending ? (
          <div className="rounded-xl bg-brand-lease/5 border border-brand-lease/20 p-4 flex items-start gap-3" data-testid="account-pending">
            <Clock className="w-5 h-5 text-brand-lease shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-brand-ink text-sm">Request pending admin approval</div>
              <div className="text-sm text-brand-muted mt-0.5">Switch <span className="capitalize font-medium">{request.current_role}</span> → <span className="capitalize font-medium text-brand-emerald">{request.requested_role}</span>. You'll keep your current role until it's approved.</div>
            </div>
          </div>
        ) : (
          <>
            {request && request.status === "approved" && (
              <div className="mb-4 rounded-xl bg-brand-sale/5 border border-brand-sale/20 p-3 flex items-center gap-2 text-sm" data-testid="account-approved">
                <CheckCircle2 className="w-4 h-4 text-brand-sale shrink-0" />
                <span className="text-brand-ink">Your switch to <span className="capitalize font-semibold">{request.requested_role}</span> was approved. Log out and back in to use it.</span>
              </div>
            )}
            {request && request.status === "rejected" && (
              <div className="mb-4 rounded-xl bg-brand-terracotta/5 border border-brand-terracotta/20 p-3 flex items-center gap-2 text-sm" data-testid="account-rejected">
                <XCircle className="w-4 h-4 text-brand-terracotta shrink-0" />
                <span className="text-brand-ink">Your previous request to become <span className="capitalize font-semibold">{request.requested_role}</span> was rejected. You can submit a new one below.</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {options.map((r) => {
                const M = ROLE_META[r];
                const Icon = M.icon;
                return (
                  <button key={r} type="button" onClick={() => setSelected(r)} data-testid={`account-role-${r}`}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${selected === r ? "border-brand-emerald bg-brand-emerald/5" : "border-brand-line hover:border-brand-emerald/40"}`}>
                    <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${selected === r ? "text-brand-emerald" : "text-brand-muted"}`} />
                    <span>
                      <span className={`block font-semibold text-sm ${selected === r ? "text-brand-emerald" : "text-brand-ink"}`}>{M.label}</span>
                      <span className="block text-xs text-brand-muted mt-0.5">{M.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <Button onClick={submit} disabled={loading || !selected} className="rounded-full bg-brand-emerald hover:bg-brand-emeraldHover mt-4" data-testid="account-submit-role">
              {loading ? "Submitting..." : "Request Role Change"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
